import React, { createContext, useContext } from 'react';
import { useAuth as useAuthHook, type UseAuthReturn, type AuthService, clearAllListPageState } from '@textile-erp/ui';
// NOTE: `ApiClient` was removed from this import because its only use
// (ApiClient.wasRecentlyRefreshed) lived in the now-disabled 15-min token
// refresh below. Re-add it here if that block is revived.
import { createApiClient, buildBackendUrl } from '@textile-erp/core';

// Create core API client for authentication
const coreApiClient = createApiClient(buildBackendUrl('/api'));

// One-time cleanup of legacy parking localStorage keys that are no longer used.
// The parking staff gate now keeps everything under `parking_staff`; these were
// written by the old staff-code-login flow and otherwise just linger in browsers.
if (typeof window !== 'undefined') {
  localStorage.removeItem('parking_staff_code');
  localStorage.removeItem('parking_checkin_verified');
}

// Setup automatic logout on 401 responses
let isLoggingOut = false;
coreApiClient.setOnUnauthorized(() => {
  // Will be set later when AuthProvider mounts
});

// localStorage key for the absolute logout deadline (time-of-day policy:
// midnight or 06:00, depending on login hour — see backend computeWebLogoutAt).
const LOGOUT_AT_KEY = 'textile_erp_logout_at';

function saveLogoutAt(logoutAt: unknown) {
  if (typeof logoutAt === 'string' || logoutAt instanceof Date) {
    const ms = new Date(logoutAt as any).getTime();
    if (!isNaN(ms)) {
      localStorage.setItem(LOGOUT_AT_KEY, String(ms));
    }
  }
}

// Configurable auth service using core API client
const authService: AuthService = {
  login: async (credentials) => {
    try {
      const response: any = await coreApiClient.post('/auth/login', credentials);
      const { user, token, logoutAt } = response;
      saveLogoutAt(logoutAt);

      return {
        user,
        token
      };
    } catch (error: any) {
      throw new Error(error.message || 'Login failed');
    }
  },

  loginByStaffCode: async (credentials) => {
    try {
      const response: any = await coreApiClient.post('/auth/login-staff-code', credentials);
      const { user, token, logoutAt } = response;
      saveLogoutAt(logoutAt);

      return {
        user,
        token
      };
    } catch (error: any) {
      throw new Error(error.message || 'Login failed');
    }
  },

  logout: async () => {
    try {
      await coreApiClient.post('/auth/logout');
    } catch (error) {
      // Logout should succeed even if API call fails
      console.warn('Logout API call failed, but continuing with local logout');
    } finally {
      // Always clear the token from API client
      coreApiClient.clearAuthToken();
      localStorage.removeItem(LOGOUT_AT_KEY);
      // Reset the parking station's selected staff so the next login starts fresh.
      localStorage.removeItem('parking_staff');
      clearAllListPageState();
    }
  },

  getCurrentUser: async () => {
    try {
      const user = await coreApiClient.get('/auth/me') as any;
      saveLogoutAt(user?.logoutAt);
      return user;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to get current user');
    }
  },

  verifyToken: async (token: string) => {
    try {
      coreApiClient.setAuthToken(token);
      const user = await coreApiClient.get('/auth/me') as any;
      saveLogoutAt(user?.logoutAt);
      return user;
    } catch (error) {
      throw new Error('Token verification failed');
    }
  }
};

const AuthContext = createContext<UseAuthReturn | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuthHook({
    authService,
    tokenKey: 'textile_erp_token',
    autoRefresh: false, // Disabled — we call /auth/refresh below instead
  });

  // Synchronize API client token with localStorage
  React.useEffect(() => {
    const token = localStorage.getItem('textile_erp_token');
    
    if (token && auth.isAuthenticated) {
      coreApiClient.setAuthToken(token);
    } else if (!auth.isAuthenticated) {
      coreApiClient.clearAuthToken();
    }
  }, [auth.isAuthenticated]);

  // Track real user activity — update lastActivityAt on any interaction
  React.useEffect(() => {
    if (!auth.isAuthenticated) return;

    // Written at most every 10 s: mousemove/scroll fire dozens of times a second, and every
    // localStorage write also wakes the other open tabs (storage event). The readers (token
    // refresh, every 15 min) only need minute-level accuracy.
    let lastWrite = 0;
    const updateActivity = () => {
      const now = Date.now();
      if (now - lastWrite < 10_000) return;
      lastWrite = now;
      localStorage.setItem('textile_erp_last_activity', now.toString());
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, updateActivity, { passive: true }));
    updateActivity(); // set on login

    return () => {
      events.forEach(e => window.removeEventListener(e, updateActivity));
    };
  }, [auth.isAuthenticated]);

  // ──────────────────────────────────────────────────────────────────────────
  // Cross-tab LOGOUT listener.
  //
  // DISABLED 2026-06-01: the proactive 15-minute token refresh that used to run
  // here is turned OFF. Web sessions now expire at a fixed time-of-day deadline
  // (midnight / 06:00 IST) set by the backend at login. refreshSession keeps the
  // SAME `expires` (no rolling extension) and the idle check is disabled, so the
  // 15-min keep-alive no longer affects WHEN logout happens — and its token
  // rotation could itself cause a stray 401 → auto-logout on a network blip.
  //
  // The full original refresh logic is kept commented below for verification;
  // remove it (and re-add the `ApiClient` import) once the new behaviour is
  // confirmed in production. The BroadcastChannel LOGOUT listener is retained so
  // a logout in one tab still propagates to the others.
  // ──────────────────────────────────────────────────────────────────────────
  React.useEffect(() => {
    if (!auth.isAuthenticated) return;

    const channel = new BroadcastChannel('textile_erp_auth');

    channel.onmessage = (event) => {
      if (event.data?.type === 'LOGOUT') {
        if (!isLoggingOut && auth.logout) {
          isLoggingOut = true;
          auth.logout().finally(() => { isLoggingOut = false; });
        }
      }
    };

    return () => {
      channel.close();
    };

    /* ── DISABLED: proactive 15-minute session refresh (kept for verification) ──
    // const IDLE_LIMIT_MS        = 8 * 60 * 60 * 1000;  // 8 hours
    const REFRESH_INTERVAL_MS  = 15 * 60 * 1000;       // 15 minutes
    const REFRESH_LOCK_KEY     = 'textile_erp_refresh_at'; // shared across tabs via localStorage

    // (TOKEN_REFRESHED branch that used to live in channel.onmessage):
    //   if (event.data?.type === 'TOKEN_REFRESHED' && event.data.token) {
    //     localStorage.setItem('textile_erp_token', event.data.token);
    //     coreApiClient.setAuthToken(event.data.token);
    //   }

    const interval = setInterval(async () => {
      const token = localStorage.getItem('textile_erp_token');
      if (!token) return;

      // Skip if another tab already refreshed within the last REFRESH_INTERVAL_MS
      const lastRefreshAt = parseInt(localStorage.getItem(REFRESH_LOCK_KEY) || '0', 10);
      if (Date.now() - lastRefreshAt < REFRESH_INTERVAL_MS) return;

      // Also skip if a 401-triggered refresh already happened recently
      if (ApiClient.wasRecentlyRefreshed(5 * 60 * 1000)) return;

      const lastActivity = parseInt(localStorage.getItem('textile_erp_last_activity') || '0', 10);
      const idleMs = Date.now() - lastActivity;

      // Claim the refresh lock before making the API call
      localStorage.setItem(REFRESH_LOCK_KEY, Date.now().toString());

      try {
        const res: any = await coreApiClient.post('/auth/refresh', { idleMs });
        const newToken = res?.token as string | undefined;
        if (newToken) {
          localStorage.setItem('textile_erp_token', newToken);
          coreApiClient.setAuthToken(newToken);
          channel.postMessage({ type: 'TOKEN_REFRESHED', token: newToken });
        }
      } catch {
        // 401 will trigger setOnUnauthorized below → auto logout
      }
    }, REFRESH_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      channel.close();
    };
    ── END DISABLED ── */
  }, [auth.isAuthenticated, auth.logout]);

  // Absolute time-of-day logout — fires exactly at the deadline set by the
  // backend (midnight or 06:00 IST, depending on login hour). Re-scheduled on
  // every auth state change so it survives page reloads.
  React.useEffect(() => {
    if (!auth.isAuthenticated) return;

    const ms = parseInt(localStorage.getItem(LOGOUT_AT_KEY) || '0', 10);
    if (!ms) return; // no deadline known yet (eg old token pre-deploy)

    const channel = new BroadcastChannel('textile_erp_auth');
    const doLogout = () => {
      if (isLoggingOut || !auth.logout) return;
      isLoggingOut = true;
      channel.postMessage({ type: 'LOGOUT' });
      auth.logout().finally(() => { isLoggingOut = false; });
    };

    const delay = ms - Date.now();
    if (delay <= 0) {
      doLogout();
      channel.close();
      return;
    }

    // setTimeout caps at ~24.8 days (int32 ms) — plenty for our ≤24h deadline.
    const t = window.setTimeout(doLogout, delay);
    return () => {
      window.clearTimeout(t);
      channel.close();
    };
  }, [auth.isAuthenticated, auth.logout]);

  // Set up automatic logout on 401 responses with loop prevention
  React.useEffect(() => {
    coreApiClient.setOnUnauthorized(() => {
      // Prevent infinite logout loops
      if (!isLoggingOut && auth.user && auth.logout) {
        isLoggingOut = true;
        auth.logout().finally(() => {
          isLoggingOut = false;
        });
      }
    });
  }, [auth.logout, auth.user]);

  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): UseAuthReturn {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}